// dependencies
import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";

// components
import Stack from "@cloudspire/legacy-resources/src/components/Stack";

// containers
import FormContainer from "./Form";
import PaginationContainer from "./Pagination";
import ResultsContainer from "./Results";

// constants
import { searchDefaultParameters } from "../../constants/search";

// contexts
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// api
import { useInifiniteRoomListResponse } from "@cloudspire/legacy-resources/src/api/room";

// libraries
import {
  get,
  flattenDeepPropertyList,
  recursiveBracket,
  generateUri,
} from "@cloudspire/legacy-shared/src/libraries";

// helpers
import { roomGetQueryParameterForDetail } from "../../helpers/room";

const getInitialParameterList = ({ query, category }) => {
  const initialParameterList = {
    category,
  };

  if (query) {
    for (const [parameterName, parameterValue] of flattenDeepPropertyList(
      query
    )) {
      if ("page" !== parameterName) {
        initialParameterList[recursiveBracket(parameterName)] = parameterValue;
      }
    }
  }

  return initialParameterList;
};

function SearchContainer(props) {
  const { contentId, category, context = "search" } = props;

  const configuration = useConfiguration();

  const route = useRoute();

  const router = useRouter();

  const { matches: matchesSm } = useViewportMedia({
    queries: [{ minWidth: 605 }],
  });

  const [parameterList, setParameterList] = useState(
    getInitialParameterList({
      query: route.getUri().getParsedQuery(),
      category,
    })
  );

  useEffect(
    function () {
      router.replace(
        generateUri({
          route,
          query: parameterList,
        }),
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [parameterList]
  );

  const perPage = parameterList?.per_page ?? 15;

  const roomsResponse = useInifiniteRoomListResponse({
    perPage,
    query: {
      ...searchDefaultParameters,
      ...parameterList,
    },
    parameterList,
    contentId,
    configuration,
  });

  const roomList = useMemo(
    () => roomsResponse.data?.roomList ?? [],
    [roomsResponse.data?.roomList]
  );

  const [showMap, setShowMap] = useState(false);

  const [shouldRenderAdditionalFilters, setShouldRenderAdditionalFilters] =
    useState(false);

  const handleChangeShouldRenderAdditionalFilters = (
    shouldRenderAdditionalFilters
  ) => {
    setShouldRenderAdditionalFilters(shouldRenderAdditionalFilters);
  };

  const $form = useRef();

  /**
   * Gère la soumission du formulaire.
   */
  function handleSubmit({ parameterList }) {
    requestAnimationFrame(function () {
      if ($form.current instanceof HTMLElement) {
        $form.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    });

    setParameterList(parameterList);
  }

  /**
   * Gère le clic sur le bouton pour réinitialiser les filtres.
   */
  function handleReset() {
    setParameterList(
      getInitialParameterList({
        category,
      })
    );
  }

  /**
   * Gère le cas où la page est redimensionnée et nécessite un rafraichissement
   *   de la requête pour mettre à jour le `per_page`.
   */
  function handleChangePagination({ perPage }) {
    const newParameterList = {
      ...parameterList,
      per_page: perPage,
    };

    setParameterList(newParameterList);
  }

  /**
   * Gère le clic sur le bouton pour afficher/cacher la carte.
   */
  function handlePressShowMap() {
    const newShowMap = !showMap;

    if (newShowMap) {
      setParameterList((parameterList) => ({
        ...parameterList,
        _city: parameterList?.city,
        _lat: parameterList?.lat,
        _lng: parameterList?.lng,
      }));
    }

    setShowMap(newShowMap);
  }

  const showMapMounted = useRef(false);
  useEffect(
    function () {
      if (!showMapMounted.current) {
        showMapMounted.current = true;
        return;
      }

      if (!showMap) {
        setParameterList((parameterList) => ({
          ...parameterList,
          city: parameterList?._city,
          lat: parameterList?._lat,
          lng: parameterList?._lng,
          _city: undefined,
          _lat: undefined,
          _lng: undefined,
        }));
      }
    },
    [showMap]
  );

  /**
   * Gère le clic sur le bouton "Plus d'hébergements".
   */
  function handlePressSeeMore() {
    roomsResponse.setSize(roomsResponse.size + 1);
  }

  /**
   * Gère le mouvement effectué sur la carte pour mettre à jour les données.
   */
  function handleChangedViewport(viewport) {
    const [lat, lng] = viewport.center;

    const newParameterList = {
      ...parameterList,
      lat,
      lng,
      city: undefined,
    };

    setParameterList(newParameterList);
  }

  const roomDetailQuery = roomGetQueryParameterForDetail({ parameterList });

  return (
    <div ref={$form}>
      <FormContainer
        showMap={showMap}
        total={roomsResponse.data?.meta?.total ?? null}
        initialParameterList={parameterList}
        shouldRenderAdditionalFilters={shouldRenderAdditionalFilters}
        parameterList={parameterList}
        onChangeShouldRenderAdditionalFilters={
          handleChangeShouldRenderAdditionalFilters
        }
        onSubmit={handleSubmit}
        onPressShowMap={handlePressShowMap}
      />

      {!shouldRenderAdditionalFilters && (
        <>
          <Stack marginTop={1} />

          <ResultsContainer
            context={context}
            perPage={perPage}
            showMap={showMap}
            onlyButtonLoading={"1" !== get(parameterList, "page", "1")}
            roomsResponse={roomsResponse}
            roomDetailQuery={roomDetailQuery}
            onChangedViewport={handleChangedViewport}
            onChangePagination={handleChangePagination}
            onPressShowMap={handlePressShowMap}
            onReset={handleReset}
          />
        </>
      )}

      {!(!matchesSm && showMap) &&
        !shouldRenderAdditionalFilters &&
        (roomsResponse.data?.meta?.total ?? 0) > roomList.length && (
          <>
            <Stack marginTop={2} />

            <PaginationContainer
              roomsResponse={roomsResponse}
              total={roomsResponse.data?.meta?.total}
              perPage={perPage}
              onPressSeeMore={handlePressSeeMore}
            />
          </>
        )}
    </div>
  );
}

SearchContainer.propTypes = {
  contentId: PropTypes.string,
  category: PropTypes.string,
  context: PropTypes.oneOf(["home", "search", "campaign", "lastSeen"]),
};

export default SearchContainer;
